import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { SITE, NAV } from "./content";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="vw-footer relative overflow-hidden" data-testid="site-footer">
      <div className="vw-grid-bg" />
      <div className="vw-container relative py-20 lg:py-28">
        {/* Hero line */}
        <div className="pb-14 border-b border-white/10">
          <div className="vw-overline vw-overline--gold mb-4">Build With Verified Workforce</div>
          <h3 className="font-display text-3xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05] max-w-4xl">
            Reliable people.
            <br />
            <span className="text-[var(--vw-gold)]">Verified results.</span>
          </h3>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="vw-btn vw-btn--primary" data-testid="footer-cta-contact">
              Discuss Your Supply Chain
              <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/delivery-partners#apply"
              className="vw-btn vw-btn--ghost"
              data-testid="footer-cta-join"
            >
              Become a Verified Partner
            </Link>
          </div>
        </div>

        <div className="pt-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <img
              src={SITE.logo}
              alt="Verified Workforce"
              className="h-14 w-auto bg-white p-2"
              data-testid="footer-logo"
            />
            <p className="mt-6 text-sm text-slate-300 leading-relaxed max-w-md">
              Verified Workforce is a construction supply-chain and procurement business
              focused on identifying, developing and connecting capable specialist contractors
              with larger construction opportunities.
            </p>
            <a
              href={SITE.contact.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-7 text-sm font-semibold"
              data-testid="footer-linkedin-link"
            >
              <span className="w-10 h-10 flex items-center justify-center border border-white/20 hover:border-[var(--vw-gold)] transition-colors">
                <Linkedin size={16} />
              </span>
              Follow on LinkedIn
            </a>
          </div>

          <div className="lg:col-span-4 grid grid-cols-2 gap-6">
            <div>
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/50 mb-5">
                Explore
              </div>
              <ul className="space-y-2.5 text-sm">
                {NAV.map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {n.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/50 mb-5">
                Legal
              </div>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link to="/privacy" data-testid="footer-privacy-link">Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/terms" data-testid="footer-terms-link">Terms</Link>
                </li>
                <li>
                  <Link to="/readiness" data-testid="footer-readiness-link">Readiness Assessment</Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/50 mb-5">
              Contact
            </div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Mail size={16} className="text-[var(--vw-gold)] mt-0.5 flex-shrink-0" />
                <a href={`mailto:${SITE.contact.email}`} data-testid="footer-email">
                  {SITE.contact.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="text-[var(--vw-gold)] mt-0.5 flex-shrink-0" />
                <a href={`tel:${SITE.contact.phoneRaw}`} data-testid="footer-phone">
                  {SITE.contact.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-[var(--vw-gold)] mt-0.5 flex-shrink-0" />
                <span>
                  {SITE.contact.addressLine1}
                  <br />
                  {SITE.contact.addressLine2}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[11px] font-mono tracking-[0.18em] uppercase text-white/40">
          <div data-testid="footer-copyright">
            © {year} Verified Workforce. Founded {SITE.contact.founded}.
          </div>
          <div>Reliable People. Verified Results.</div>
        </div>
      </div>
    </footer>
  );
}
